import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ChangeReferenceDto } from './../../../../../data-transfer/entities/change-reference-dto';
import { getImportTableCallers, getImportTableSortField } from './import-table-columns';
import { ChangeRow, ImportRow, mapChangeRow, mapImportRow } from './import-table-rows';
import { ImportCandidateDto } from './../../../../../data-transfer/entities/import-candidate-dto';
import { Component, Input, Output, EventEmitter, ViewChild, OnDestroy, input, signal, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { OrganizationDto } from 'src/app/data-transfer/entities/organization-dto';
import { FilterMenuComponent } from 'src/app/components/shared-components/menus/filter-menu/filter-menu.component';
import { PackagingUnitTypeService } from 'src/app/navigation/services/packaging-unit-type-service';
import { ComponentTypeService } from 'src/app/navigation/services/component-type-service';
import { DictionaryHandler } from 'src/app/model/dictionary';
import { Subscription, switchMap } from 'rxjs';

@Component({
  selector: 'app-import-html',
  templateUrl: './import-html.component.html',
  styleUrls: ['./import-html.component.scss']
})
export class ImportHtmlComponent implements OnDestroy {

  private readonly importExportApiService = inject(ImportExportApiService);
  private readonly packagingUnitTypeService = inject(PackagingUnitTypeService);
  private readonly componentTypeService = inject(ComponentTypeService);
  private readonly dictionaryHandler = inject(DictionaryHandler);

  readonly packagingTypeOptions = this.packagingUnitTypeService.getPackagingUnitTypes().map(type => ({ name: type.name, value: type.id }));
  readonly componentCategoryOptions = this.dictionaryHandler.getComponentCategoriesDictionary().map(category => ({ name: category.value, value: category.key }));
  readonly componentSubtypeOptions = this.componentTypeService.getAllComponentSubtypes().map(subtype => ({ name: subtype.name, value: subtype.id }));

  @Input() displayedColumns: string[] = [];
  @Input() selectedCandidateIds: Set<number> = new Set<number>();
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];
  readonly isChange = input(false);
  readonly callers = computed(() => getImportTableCallers(this.isChange()));

  readonly packagingPart = input.required<PackagingPart>();

  private readonly organizations = toSignal(
    toObservable(this.packagingPart).pipe(switchMap(part => this.importExportApiService.getExportOrganizations(part))),
    { initialValue: [] as OrganizationDto[] }
  );
  readonly organizationNameById = computed(() => new Map(this.organizations().map(org => [org.id, org.name])));
  readonly organizationOptions = computed(() =>
    this.organizations()
      .map(org => ({ name: org.name, value: org.id }))
      .sort((a, b) => a.name.localeCompare(b.name))
  );

  @Output() candidateIdsSelected = new EventEmitter<Set<number>>();
  @Output() navigationClicked = new EventEmitter();
  @Output() pageSizeChanged = new EventEmitter<number>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('filterComponent') filterMenu?: FilterMenuComponent;

  readonly rows = signal<(ImportRow | ChangeRow)[]>([]);
  allCandidateIds: number[] = [];
  totalItemCount = 0;
  currentPageIndex = 0;
  filteredColumns: string[] = [];

  private readonly isLoadingSignal = signal(false);
  readonly isLoading = this.isLoadingSignal.asReadonly();
  private readonly currentPageSizeSignal = signal(10);
  readonly currentPageSize = this.currentPageSizeSignal.asReadonly();
  readonly loadingPlaceholderRows = computed(() => Array.from({ length: this.currentPageSizeSignal() }, () => ({})));

  paginationDataSource?: PaginationDataSource<ImportCandidateDto> | PaginationDataSource<ChangeReferenceDto>;
  private paginationSubscription?: Subscription;


  get isPackagingSystem(): boolean {
    return this.packagingPart() === PackagingPart.System;
  }

  get isPackagingUnit(): boolean {
    return this.packagingPart() === PackagingPart.Unit;
  }

  get isComponent(): boolean {
    return this.packagingPart() === PackagingPart.Component;
  }

  get isMaterial(): boolean {
    return this.packagingPart() === PackagingPart.Material;
  }

  get isAllCandidateIdsSelected(): boolean {
    return this.allCandidateIds.length > 0 && this.allCandidateIds.every(id => this.selectedCandidateIds.has(id));
  }

  ngOnDestroy(): void {
    this.paginationSubscription?.unsubscribe();
  }

  setDataSource(updatedDataSource: PaginationDataSource<ImportCandidateDto> | PaginationDataSource<ChangeReferenceDto>, pageSize?: number) {
    this.paginationSubscription?.unsubscribe();

    if (pageSize !== undefined) {
      this.currentPageSizeSignal.set(pageSize);
    }

    if (this.paginationDataSource && this.paginationDataSource !== updatedDataSource) {
      this.filterMenu?.clearAll();
    }

    this.paginationDataSource = updatedDataSource;

    const dataSource: PaginationDataSource<any> = updatedDataSource;

    const pageSubscription = dataSource.page.subscribe(page => {
      this.totalItemCount = page.totalItemCount;
      this.currentPageIndex = page.pageIndex;
      this.allCandidateIds = page.allIds;
      this.pruneSelectedCandidateIds(page.allIds);
      this.applyMappedData(page.items);
    });
    const loadingSubscription = dataSource.isLoading.subscribe(loading => {
      this.isLoadingSignal.set(loading);
    });
    const filteredColumnsSubscription = dataSource.filteredColumns.subscribe(filteredColumns => {
      this.filteredColumns = filteredColumns;
    });
    this.paginationSubscription = new Subscription();
    this.paginationSubscription.add(pageSubscription);
    this.paginationSubscription.add(loadingSubscription);
    this.paginationSubscription.add(filteredColumnsSubscription);
  }

  onPageChanged(event: PageEvent) {
    this.currentPageSizeSignal.set(event.pageSize);
    this.pageSizeChanged.emit(event.pageSize);
    this.paginationDataSource?.fetchData(event.pageIndex, event.pageSize);
  }

  private pruneSelectedCandidateIds(allIds: number[]) {
    const prunedIds = new Set([...this.selectedCandidateIds].filter(id => allIds.includes(id)));
    if (prunedIds.size !== this.selectedCandidateIds.size) {
      this.selectedCandidateIds = prunedIds;
      this.candidateIdsSelected.emit(prunedIds);
    }
  }

  onSortChanged(event: Sort) {
    if (!this.paginationDataSource) {
      return;
    }

    const fieldName = this.getPaginationFieldName(event.active);
    if (!fieldName) {
      return;
    }

    if (event.direction) {
      this.paginationDataSource.sort(fieldName, event.direction === 'asc');
    } else {
      this.paginationDataSource.removeSorting();
    }
  }

  private applyMappedData(entries: (ImportCandidateDto | ChangeReferenceDto)[]) {
    this.rows.set(
      entries.map(entry => (this.isChange() ? mapChangeRow(entry as ChangeReferenceDto) : mapImportRow(entry as ImportCandidateDto, this.packagingPart())))
    );
  }

  navigateToCandidate(candidate: ImportRow | ChangeRow, navigateToCurrentVersion?: boolean) {
    this.navigationClicked.emit({ candidate, navigateToCurrentVersion });
  }

  selectAll() {
    const selectAll = !this.isAllCandidateIdsSelected;
    this.selectedCandidateIds = new Set<number>(selectAll ? this.allCandidateIds : []);
    this.candidateIdsSelected.emit(this.selectedCandidateIds);
  }

  isSelectedCandidate(candidate: ImportRow | ChangeRow): boolean {
    return this.selectedCandidateIds.has(candidate.itemId);
  }

  setSelectedCandidates(checked: boolean, candidate: ImportRow | ChangeRow) {
    const selectedCandidateIds = new Set(this.selectedCandidateIds);
    if (checked) {
      selectedCandidateIds.add(candidate.itemId);
    } else {
      selectedCandidateIds.delete(candidate.itemId);
    }
    this.selectedCandidateIds = selectedCandidateIds;
    this.candidateIdsSelected.emit(this.selectedCandidateIds);
  }

  private getPaginationFieldName(column: string): string | undefined {
    return getImportTableSortField(column, this.isChange());
  }
}
